'use client'

import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { zodResolver } from '@hookform/resolvers/zod'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material'
import { usePathname, useRouter } from 'next/navigation'

import { DynamicField, isFieldVisible } from './DynamicField'
import { SummaryStep } from './SummaryStep'
import { RecoverDraftDialogs } from '@/src/components/recover/RecoverDraftDialogs'
import { ArrowLeftIcon, ArrowRightIcon } from '@/src/components/ui/ArrowIcons'
import { buildDefaultValues, buildStepSchema } from '@/src/lib/flow-engine/buildSchema'
import type { FlowStepConfig } from '@/src/lib/flow-engine/types'
import { getAvailableStructures } from '@/src/services/codes.api'
import { loadDraft, recoverDraft } from '@/src/services/drafts.api'
import { isStructureExhausted, resolveLookupOptions } from '@/src/services/lookups-resolver.api'
import type { AdmissionLoadResponse } from '@/src/types/api.types'

interface LookupOption {
  value: string
  label: string
}

interface StructureModalOption {
  value: string
  label: string
  structureName: string
  badge: string
  isFull: boolean
}

interface ConfigDrivenStepProps {
  /** Step configuration from the flow */
  stepConfig: FlowStepConfig
  /** All steps (needed for summary) */
  allSteps: FlowStepConfig[]
  /** Previously saved form data for all steps, keyed by "step{id}" */
  savedFormData: Record<string, Record<string, unknown>>
  /** Pre-resolved display labels for field values, keyed by field key */
  fieldLabels?: Record<string, string>
  /** Called when this step is submitted with valid data */
  onSubmit: (_data: Record<string, unknown>) => Promise<void> | void
  /** Called when a field's display label is resolved (for persisting in store) */
  onFieldLabelResolved?: (_labels: Record<string, string>) => void
  /** Called when user clicks Back */
  onBack?: () => void
  /** Called when a draft is successfully loaded; receives draftId + API steps array.
   *  Must populate the store and return the slug of the next step to navigate to. */
  onDraftRestored?: (
    _draftId: string,
    _apiSteps: { values: { name: string; value: string }[] }[],
    _isWorker: boolean
  ) => string
  /** Whether this is the first step (hides Back button) */
  isFirstStep?: boolean
  /** Loading state for the submit button */
  isSubmitting?: boolean
}

export const ConfigDrivenStep: React.FC<ConfigDrivenStepProps> = ({
  stepConfig,
  allSteps,
  savedFormData,
  fieldLabels,
  onSubmit,
  onFieldLabelResolved,
  onDraftRestored,
  onBack,
  isFirstStep = false,
  isSubmitting = false
}) => {
  const stepKey = `step${stepConfig.id}`

  // ── Summary layout ─────────────────────────────────────
  if (stepConfig.layout === 'summary') {
    return (
      <SummaryStep
        allSteps={allSteps}
        savedFormData={savedFormData}
        fieldLabels={fieldLabels ?? {}}
        onSubmit={() => onSubmit({})}
        onBack={onBack}
        isSubmitting={isSubmitting}
      />
    )
  }

  const allSavedValues = Object.values(savedFormData).reduce<Record<string, unknown>>(
    (acc, stepData) => ({ ...acc, ...stepData }),
    {}
  )
  const schema = buildStepSchema(stepConfig, allSavedValues)
  const defaults = buildDefaultValues(stepConfig, savedFormData[stepKey])

  return (
    <ConfigDrivenStepForm
      stepConfig={stepConfig}
      allSteps={allSteps}
      schema={schema}
      defaults={defaults}
      savedFormData={savedFormData}
      onSubmit={onSubmit}
      onFieldLabelResolved={onFieldLabelResolved}
      onDraftRestored={onDraftRestored}
      onBack={onBack}
      isFirstStep={isFirstStep}
      isSubmitting={isSubmitting}
    />
  )
}

/* ── Inner form component (needs to call useForm) ──────── */

interface FormInnerProps {
  stepConfig: FlowStepConfig
  allSteps: FlowStepConfig[]
  schema: ReturnType<typeof buildStepSchema>
  defaults: Record<string, unknown>
  savedFormData: Record<string, Record<string, unknown>>
  onSubmit: (_data: Record<string, unknown>) => Promise<void> | void
  onFieldLabelResolved?: (_labels: Record<string, string>) => void
  onDraftRestored?: (
    _draftId: string,
    _apiSteps: { values: { name: string; value: string }[] }[],
    _isWorker: boolean
  ) => string
  onBack?: () => void
  isFirstStep: boolean
  isSubmitting: boolean
}

function ConfigDrivenStepForm({
  stepConfig,
  allSteps,
  schema,
  defaults,
  savedFormData,
  onSubmit,
  onFieldLabelResolved,
  onDraftRestored,
  onBack,
  isFirstStep,
  isSubmitting
}: FormInnerProps) {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const pathname = usePathname()
  const safePathname = pathname ?? ''
  const locale = safePathname.split('/')[1] || 'it'
  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: defaults
  })

  // Re-sync form values when navigating back to a step with updated saved data
  useEffect(() => {
    methods.reset(defaults)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepConfig.id])

  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [openRecoverDialog, setOpenRecoverDialog] = useState(false)
  const [openedFromEmailBlur, setOpenedFromEmailBlur] = useState(false)
  const [openRecoverEmail, setOpenRecoverEmail] = useState(false)
  const [recoverEmail, setRecoverEmail] = useState('')
  const [loadingDraft, setLoadingDraft] = useState(false)
  const [loadingRecover, setLoadingRecover] = useState(false)
  const [recoverError, setRecoverError] = useState<string | null>(null)
  const [recoverSuccess, setRecoverSuccess] = useState(false)
  const [draftLoaded, setDraftLoaded] = useState(false)
  const [remoteSummaryValueLabels, setRemoteSummaryValueLabels] = useState<Record<string, string>>(
    {}
  )
  const [expandedSideSummaryGroupId, setExpandedSideSummaryGroupId] = useState<number | null>(null)
  const [fullStructureModalOpen, setFullStructureModalOpen] = useState(false)
  const [checkingStructureAvailability, setCheckingStructureAvailability] = useState(false)
  const [altCityOptions, setAltCityOptions] = useState<LookupOption[]>([])
  const [altStructureOptions, setAltStructureOptions] = useState<StructureModalOption[]>([])
  const [altRoomOptions, setAltRoomOptions] = useState<LookupOption[]>([])
  const [altCity, setAltCity] = useState('')
  const [altStructure, setAltStructure] = useState('')
  const [altRoom, setAltRoom] = useState('')
  const [modalBusy, setModalBusy] = useState(false)
  const [pendingStepData, setPendingStepData] = useState<Record<string, unknown> | null>(null)
  const [fullStructureName, setFullStructureName] = useState('')
  const [submitLock, setSubmitLock] = useState(false)
  const [cityCount, setCityCount] = useState<number | null>(null)
  const [forceWaitlistModalDebug, setForceWaitlistModalDebug] = useState(false)

  const allValues = methods.watch()
  const isCentered = stepConfig.layout === 'centered'
  const isAccommodationStep = ['alloggio', 'accommodation'].includes(stepConfig.slug)
  const showWaitlistDebugTrigger = process.env.NODE_ENV !== 'production' && isAccommodationStep
  const isWorkerJourney = safePathname.includes('/booking-worker/')
  const journey = isWorkerJourney ? 'worker' : 'student'
  const lookupPrefix = isWorkerJourney
    ? '/api/booking-worker/lookups'
    : '/api/booking-student/lookups'
  const isInteractionLocked = isSubmitting || checkingStructureAvailability || submitLock

  // Merge saved values from previous steps with current form values (for remote options dependencies)
  // Current form values (allValues) take priority over saved data so that live changes
  // to fields like bando_inps immediately affect within-step visibility conditions.
  const mergedValues = {
    ...Object.values(savedFormData).reduce((acc, stepData) => ({ ...acc, ...stepData }), {}),
    ...allValues
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const debugValue = params.get('debugWaitlistModal') ?? params.get('debugFullStructure') ?? ''

    setForceWaitlistModalDebug(
      ['1', 'true', 'yes', 'on'].includes(String(debugValue).toLowerCase())
    )
  }, [])

  useEffect(() => {
    if (!['citta', 'city'].includes(stepConfig.slug)) {
      setCityCount(null)
      return
    }

    let cancelled = false

    resolveLookupOptions({
      endpoint: `${lookupPrefix}/cities/`,
      lang: (i18n.resolvedLanguage ?? 'it').slice(0, 2)
    })
      .then((data: Record<string, unknown>[]) => {
        if (cancelled) return
        setCityCount(Array.isArray(data) ? data.length : null)
      })
      .catch(() => {
        if (!cancelled) setCityCount(null)
      })

    return () => {
      cancelled = true
    }
  }, [i18n.resolvedLanguage, lookupPrefix, stepConfig.slug])

  const fetchCities = async () => {
    const data = await resolveLookupOptions({
      endpoint: `${lookupPrefix}/cities/`,
      lang: (i18n.resolvedLanguage ?? 'it').slice(0, 2)
    })
    return data.map((item, index) => ({
      value: String(item.slug ?? item.value ?? item.name ?? item.label ?? `city-${index}`),
      label: String(item.name ?? item.label ?? item.slug ?? item.value ?? `city-${index}`)
    }))
  }

  const fetchStructures = async (citySlug: string, checkin?: string, gender?: string) => {
    const lang = (i18n.resolvedLanguage ?? 'it').slice(0, 2)
    const target = isWorkerJourney ? 'lavoratori' : 'studenti'

    // Use availability endpoint when we have checkin + gender (modal context)
    if (checkin && gender) {
      const formattedCheckin = formatDateForAPI(checkin)
      const raw = await getAvailableStructures(citySlug, formattedCheckin, {
        gender,
        target,
        lang
      })

      const items = Array.isArray(raw) ? raw : []

      return items.map((item, index) => {
        const fallbackName = String(
          (item as Record<string, unknown>).name ??
            (item as Record<string, unknown>).label ??
            (item as Record<string, unknown>).slug ??
            ''
        )
        const rooms = (item as Record<string, unknown>).rooms as
          | { esaurite?: boolean; name?: string | Record<string, unknown>; label?: string }
          | undefined
        const isFull = rooms?.esaurite === true
        const badge =
          rooms?.name && typeof rooms.name === 'object'
            ? String((rooms.name as Record<string, unknown>)[lang] ?? '')
            : String(rooms?.name ?? rooms?.label ?? '')
        return {
          value: String(
            (item as Record<string, unknown>).slug ??
              (item as Record<string, unknown>).value ??
              fallbackName ??
              `structure-${index}`
          ),
          label: fallbackName,
          structureName: fallbackName,
          badge,
          isFull
        }
      })
    }

    // Fallback: generic lookups (no availability info)
    const data = await resolveLookupOptions({
      endpoint: `${lookupPrefix}/structures/`,
      lang,
      parentValue: citySlug
    })

    return data.map((item, index) => {
      const fallbackName = String(item.name ?? item.label ?? item.slug ?? '')
      return {
        value: String(item.slug ?? item.value ?? fallbackName ?? `structure-${index}`),
        label: fallbackName,
        structureName: fallbackName,
        badge: '',
        isFull: false
      }
    })
  }

  const fetchRooms = async (structureSlug: string) => {
    const data = await resolveLookupOptions({
      endpoint: `${lookupPrefix}/rooms/`,
      lang: (i18n.resolvedLanguage ?? 'it').slice(0, 2),
      parentValue: structureSlug
    })

    return data.map((item, index) => ({
      value: String(item.slug ?? item.value ?? item.name ?? item.label ?? `room-${index}`),
      label: String(item.name ?? item.label ?? item.slug ?? item.value ?? `room-${index}`)
    }))
  }

  /**
   * Format date to YYYY-MM-DD format for API (uses local timezone to avoid off-by-one day)
   */
  const formatDateForAPI = (date: unknown): string => {
    if (!date) return ''
    if (typeof date === 'string') {
      // If already a string, check if it's in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date
      // Try to parse and reformat
      const parsed = new Date(date)
      if (!isNaN(parsed.getTime())) {
        const y = parsed.getFullYear()
        const m = String(parsed.getMonth() + 1).padStart(2, '0')
        const d = String(parsed.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
      }
      return date
    }
    if (date instanceof Date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }
    return String(date)
  }

  /**
   * Check room availability using the internal API proxy
   * GET /api/booking-student|booking-worker/lookups/availability
   * Returns { rooms: { esaurite: boolean } }
   */
  const checkRoomAvailability = async (
    sede: string,
    checkin: string,
    durata: string,
    gender: string,
    tipologia: string
  ): Promise<boolean> => {
    try {
      const lang = (i18n.resolvedLanguage ?? 'it').slice(0, 2)

      // Format checkin date to YYYY-MM-DD
      const formattedCheckin = formatDateForAPI(checkin)

      console.log('📡 [checkRoomAvailability] Calling availability endpoint with:', {
        sede,
        checkin,
        durata,
        gender,
        tipologia
      })

      const isExhausted = await isStructureExhausted({
        journey,
        sede,
        checkin: formattedCheckin,
        durata,
        gender,
        tipologia,
        lang
      })

      console.log('✅ [checkRoomAvailability] Result:', {
        sede,
        checkin,
        durata,
        gender,
        tipologia,
        isExhausted
      })

      return isExhausted
    } catch (error) {
      console.error('❌ [checkRoomAvailability] Error:', error)
      return false
    }
  }

  const openFullStructureModal = async (
    selectedCity: string,
    selectedStructure: string,
    checkin?: string,
    gender?: string
  ) => {
    setModalBusy(true)
    setFullStructureModalOpen(true)

    try {
      const cities = await fetchCities()
      setAltCityOptions(cities)

      const resolvedCity = selectedCity || cities[0]?.value || ''
      setAltCity(resolvedCity)

      if (!resolvedCity) {
        setAltStructureOptions([])
        setAltRoomOptions([])
        setAltStructure('')
        setAltRoom('')
        setFullStructureName(selectedStructure || t('fields.sede_camplus_1.label'))
        return
      }

      const structures = await fetchStructures(resolvedCity, checkin, gender)
      setAltStructureOptions(structures)

      const selectedStructureItem = structures.find((opt) => opt.value === selectedStructure)
      setFullStructureName(selectedStructureItem?.structureName ?? selectedStructure)

      // Keep only city preselected when modal opens; user must choose structure and room.
      setAltStructure('')
      setAltRoomOptions([])
      setAltRoom('')
    } catch {
      setAltCityOptions([])
      setAltStructureOptions([])
      setAltRoomOptions([])
      setAltRoom('')
    } finally {
      setModalBusy(false)
    }
  }

  const handleFormSubmit = methods.handleSubmit(
    async (_data) => {
      if (isInteractionLocked) return
      setSubmitLock(true)

      console.log(`✅ [Step ${stepConfig.id}] VALIDAZIONE RIUSCITA`, {
        step: stepConfig.id,
        stepTitle: stepConfig.title,
        validatedData: _data,
        allFieldsData: Object.keys(_data).reduce(
          (acc, key) => {
            acc[key] = _data[key]
            return acc
          },
          {} as Record<string, unknown>
        )
      })

      try {
        if (isAccommodationStep) {
          const selectedCity = String(allValues['citta_1'] ?? allSavedValues['citta_1'] ?? '')
          const selectedStructure = String(_data.sede_camplus_1 ?? '')
          const checkin = String(_data['checkin'] ?? '')
          const durata = String(_data['durata'] ?? '')
          const roomType = String(_data['sistemazione_1'] ?? '')
          const gender = String(_data.sesso ?? allValues.sesso ?? allSavedValues.sesso ?? '')

          console.log('🏢 [handleFormSubmit] Accommodation Step - Checking room availability', {
            selectedCity,
            selectedStructure,
            checkin,
            durata,
            roomType,
            gender,
            forceWaitlistModalDebug,
            hasAllParams: Boolean(selectedStructure && checkin && durata && roomType && gender)
          })

          if (forceWaitlistModalDebug) {
            setCheckingStructureAvailability(true)
            try {
              setPendingStepData(_data as Record<string, unknown>)
              await openFullStructureModal(selectedCity, selectedStructure, checkin, gender)
              return
            } finally {
              setCheckingStructureAvailability(false)
            }
          }

          if (selectedStructure && checkin && durata && roomType && gender) {
            setCheckingStructureAvailability(true)
            try {
              console.log('📡 [handleFormSubmit] Calling checkRoomAvailability with:', {
                selectedStructure,
                checkin,
                durata,
                gender,
                roomType
              })
              const isExhausted = await checkRoomAvailability(
                selectedStructure,
                checkin,
                durata,
                gender,
                roomType
              )

              console.log('✅ [handleFormSubmit] checkRoomAvailability result:', { isExhausted })

              if (isExhausted) {
                setPendingStepData(_data as Record<string, unknown>)
                await openFullStructureModal(selectedCity, selectedStructure, checkin, gender)
                return
              }

              // Check completed successfully and room is available → proceed with submit
              await onSubmit(_data as Record<string, unknown>)
            } catch (error) {
              // API error: show error message and prevent submit
              console.error('❌ [handleFormSubmit] Availability check error:', error)
              // Could show toast/error UI here
            } finally {
              setCheckingStructureAvailability(false)
            }
          } else {
            console.error(
              '❌ [handleFormSubmit] Cannot submit: Missing parameters for availability check:',
              {
                selectedStructure: !selectedStructure,
                checkin: !checkin,
                durata: !durata,
                roomType: !roomType,
                gender: !gender
              }
            )
            // Prevent submit if parameters are missing
          }
        } else {
          // Not an accommodation step → submit normally
          await onSubmit(_data as Record<string, unknown>)
        }
      } finally {
        setSubmitLock(false)
      }
    },
    (errors) => {
      console.error(`❌ [Step ${stepConfig.id}] ERRORI DI VALIDAZIONE`, {
        step: stepConfig.id,
        stepTitle: stepConfig.title,
        errors: errors,
        formValues: methods.getValues(),
        fieldKeys: stepConfig.fields.map((f) => ({
          key: f.key,
          type: f.type,
          required: f.required,
          currentValue: methods.getValues(f.key),
          hasError: !!errors[f.key]
        }))
      })
    }
  )

  const allSavedValues = useMemo(
    () => Object.values(savedFormData).reduce((acc, stepData) => ({ ...acc, ...stepData }), {}),
    [savedFormData]
  )

  const handleEmailDraftFound = (foundEmail: string) => {
    setEmail(foundEmail)
    setRecoverEmail(foundEmail)
    setCode('')
    setRecoverError(null)
    setRecoverSuccess(false)
    setDraftLoaded(false)
    setOpenRecoverEmail(false)
    setOpenedFromEmailBlur(true)
    setOpenRecoverDialog(true)
  }

  const handleFetchDraft = async () => {
    if (!email.trim() || !code.trim()) {
      setRecoverError(t('recover.missingCredentials'))
      return
    }

    setLoadingDraft(true)
    setRecoverError(null)
    setDraftLoaded(false)

    try {
      const data: AdmissionLoadResponse = await loadDraft(email.trim(), code.trim())

      if (data.error) {
        setRecoverError(data.errorMessages?.[0] || data.message || t('recover.error'))
      } else if (data.body && !data.body.closed && data.body.steps) {
        const occupazione = data.body.steps?.[0]?.values?.find((v) => v.name === 'occupazione')
        const isWorker = occupazione?.value === '1'
        const basePath = isWorker ? `/${locale}/booking-worker` : `/${locale}/booking-student`

        setDraftLoaded(true)

        if (onDraftRestored) {
          const nextSlug = onDraftRestored(data.body.id, data.body.steps, isWorker)
          router.push(`${basePath}/${nextSlug}/`)
        } else {
          // Fallback: redirect with query params
          const currentSaveStep = data.body.steps?.length || 1
          const stepSlug =
            [...allSteps].reverse().find((step) => (step.saveStep ?? step.id) === currentSaveStep)
              ?.slug ?? 'registration'
          router.push(
            `${basePath}/${stepSlug}/?draftId=${data.body.id}&email=${encodeURIComponent(email.trim())}&code=${encodeURIComponent(code.trim())}`
          )
        }
      } else {
        setRecoverError(t('recover.noDraftsFound'))
      }
    } catch {
      setRecoverError(t('recover.error'))
    } finally {
      setLoadingDraft(false)
    }
  }

  const handleRequestCode = async () => {
    if (!recoverEmail.trim()) {
      setRecoverError(t('recover.emailRequired'))
      return
    }

    setLoadingRecover(true)
    setRecoverError(null)
    setRecoverSuccess(false)

    try {
      const data = await recoverDraft(
        recoverEmail.trim(),
        (i18n.resolvedLanguage ?? 'it').slice(0, 2)
      )

      if (data?.returnCode === 'OK') {
        setRecoverSuccess(true)
        setRecoverEmail('')
      } else {
        setRecoverError(t('recover.error'))
      }
    } catch {
      setRecoverError(t('recover.error'))
    } finally {
      setLoadingRecover(false)
    }
  }

  const recoverDialogs = (
    <RecoverDraftDialogs
      open={openRecoverDialog}
      onOpenChange={(open) => {
        setOpenRecoverDialog(open)
        if (!open) setOpenedFromEmailBlur(false)
      }}
      titleOverride={openedFromEmailBlur ? t('recover.titleFromEmailBlur') : undefined}
      openRecoverEmail={openRecoverEmail}
      onOpenRecoverEmailChange={setOpenRecoverEmail}
      email={email}
      setEmail={setEmail}
      code={code}
      setCode={setCode}
      recoverEmail={recoverEmail}
      setRecoverEmail={setRecoverEmail}
      loading={loadingDraft}
      loadingRecover={loadingRecover}
      error={recoverError}
      draftLoaded={draftLoaded}
      recoverSuccess={recoverSuccess}
      handleFetchDraft={handleFetchDraft}
      handleRequestCode={handleRequestCode}
    />
  )

  const handleAlternativeCityChange = async (nextCity: string) => {
    setAltCity(nextCity)
    setAltStructure('')
    setAltRoom('')
    setAltRoomOptions([])
    setModalBusy(true)
    try {
      const checkin = formatDateForAPI(
        pendingStepData?.['checkin'] ?? allSavedValues['checkin'] ?? ''
      )
      const gender = String(pendingStepData?.sesso ?? allSavedValues.sesso ?? '')
      const structures = await fetchStructures(nextCity, checkin, gender)
      setAltStructureOptions(structures)
    } catch {
      setAltStructureOptions([])
      setAltRoomOptions([])
    } finally {
      setModalBusy(false)
    }
  }

  const handleAlternativeStructureChange = async (nextStructure: string) => {
    setAltStructure(nextStructure)
    setAltRoom('')
    setModalBusy(true)
    try {
      const rooms = await fetchRooms(nextStructure)
      setAltRoomOptions(rooms)
    } catch {
      setAltRoomOptions([])
    } finally {
      setModalBusy(false)
    }
  }

  const closeFullStructureModal = () => {
    setFullStructureModalOpen(false)
    setPendingStepData(null)
  }

  const openWaitlistModalForDebug = async () => {
    if (!isAccommodationStep) return

    const currentValues = methods.getValues() as Record<string, unknown>
    const selectedCity = String(currentValues['citta_1'] ?? allSavedValues['citta_1'] ?? '')
    const selectedStructure = String(currentValues.sede_camplus_1 ?? '')

    const checkin = formatDateForAPI(currentValues['checkin'] ?? allSavedValues['checkin'] ?? '')
    const gender = String(currentValues.sesso ?? allSavedValues.sesso ?? '')

    setPendingStepData(currentValues)
    setCheckingStructureAvailability(true)
    try {
      await openFullStructureModal(selectedCity, selectedStructure, checkin, gender)
    } finally {
      setCheckingStructureAvailability(false)
    }
  }

  const handleConfirmAlternative = () => {
    if (!pendingStepData || !altCity || !altStructure || !altRoom) return

    closeFullStructureModal()
    void onSubmit({
      ...pendingStepData,
      citta_1: altCity,
      sede_camplus_1: altStructure,
      sistemazione_1: altRoom,
      richiestaListaAttesa: false
    })
  }

  const handleRequestWaitlist = () => {
    if (!pendingStepData) return

    closeFullStructureModal()
    void onSubmit({
      ...pendingStepData,
      citta_1: altCity || allSavedValues['citta_1'],
      sede_camplus_1: altStructure || pendingStepData.sede_camplus_1,
      sistemazione_1: altRoom || pendingStepData['sistemazione_1'],
      richiestaListaAttesa: true,
      __terminateBooking: true
    })
  }

  const renderFormInteractionOverlay = () => {
    if (!isInteractionLocked) return null

    return (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 20,
          backgroundColor: 'rgba(255, 255, 255, 0.72)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'all'
        }}
      >
        <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
          <CircularProgress size={30} />
          <Typography variant="body2" color="text.secondary">
            {t('common.loading', { defaultValue: 'Attendere...' })}
          </Typography>
        </Stack>
      </Box>
    )
  }

  useEffect(() => {
    let cancelled = false

    const loadRemoteSummaryLabels = async () => {
      const labels: Record<string, string> = {}

      for (const step of allSteps) {
        if (step.id >= stepConfig.id) break

        const data = savedFormData[`step${step.id}`]
        if (!data) continue

        for (const field of step.fields) {
          if (!field.remoteOptions) continue

          const value = data[field.key]
          if (value === undefined || value === '' || value === null) continue

          const valueString = String(value)
          const cacheKey = `${step.id}:${field.key}:${valueString}`
          let parentValue: string | undefined

          if (field.remoteOptions.dependsOn) {
            const rawParentValue = allSavedValues[field.remoteOptions.dependsOn]
            if (rawParentValue === undefined || rawParentValue === '' || rawParentValue === null) {
              continue
            }
            parentValue = String(rawParentValue)
          }

          try {
            const options = await resolveLookupOptions({
              endpoint: field.remoteOptions.endpoint,
              lang: (i18n.resolvedLanguage ?? 'it').slice(0, 2),
              parentValue
            })
            const vk = field.remoteOptions.valueKey ?? 'value'
            const lk = field.remoteOptions.labelKey ?? 'label'
            const matched = options.find(
              (option) =>
                String(option[vk] ?? option.value ?? option.slug ?? option.id ?? '') === valueString
            )

            if (matched) {
              labels[cacheKey] = String(
                matched[lk] ?? matched.label ?? matched.name ?? matched.nome ?? valueString
              )
            }
          } catch {
            // Keep fallback raw values if lookup fails.
          }
        }
      }

      if (!cancelled) {
        setRemoteSummaryValueLabels(labels)
      }
    }

    void loadRemoteSummaryLabels()

    return () => {
      cancelled = true
    }
  }, [allSavedValues, allSteps, i18n.resolvedLanguage, savedFormData, stepConfig.id])

  // ── Resolve title (with optional template interpolation) ──
  const titleVars = Object.fromEntries(
    Object.entries(allSavedValues).map(([k, v]) => [k, String(v ?? '').toUpperCase()])
  )

  const titleInterpolationValues = {
    ...titleVars,
    cityCount: cityCount
  }
  const stepTitle = stepConfig.titleTemplate
    ? t(stepConfig.titleTemplate, titleInterpolationValues)
    : t(stepConfig.title ?? `steps.${stepConfig.slug}.title`, titleInterpolationValues)
  const stepDescription = stepConfig.description ? t(stepConfig.description) : ''

  // Build side-summary sections from all previous steps
  const sideSummarySections = useMemo(
    () =>
      stepConfig.showSideSummary
        ? buildSideSummarySections(stepConfig, savedFormData, allSteps, t, remoteSummaryValueLabels)
        : [],
    [allSteps, remoteSummaryValueLabels, savedFormData, stepConfig, t]
  )

  useEffect(() => {
    if (!sideSummarySections.length) {
      setExpandedSideSummaryGroupId(null)
      return
    }

    const previousGroupId = sideSummarySections[sideSummarySections.length - 1].groupId
    setExpandedSideSummaryGroupId((current) =>
      current && sideSummarySections.some((section) => section.groupId === current)
        ? current
        : previousGroupId
    )
  }, [sideSummarySections])

  // ── Centered layout (intro steps) ─────────────────────
  if (isCentered) {
    return (
      <>
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleFormSubmit}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              maxWidth: 822,
              gap: { xs: 4, md: 6 }
            }}
          >
            <Typography
              variant="h2"
              sx={{ color: 'primary.main', textAlign: 'center', maxWidth: 822 }}
            >
              {stepTitle}
            </Typography>

            {stepDescription ? (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                {stepDescription}
              </Typography>
            ) : null}

            <Box
              sx={{
                width: '100%',
                maxWidth: 400,
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }}
            >
              {stepConfig.fields.map((field) => (
                <DynamicField
                  key={field.key}
                  field={field}
                  allValues={mergedValues}
                  onEmailDraftFound={handleEmailDraftFound}
                  onFieldLabelResolved={onFieldLabelResolved}
                />
              ))}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: isFirstStep ? 'center' : 'space-between',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                {!isFirstStep && onBack && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={onBack}
                    disabled={isInteractionLocked}
                    sx={{ color: 'text.secondary', borderColor: 'divider' }}
                    startIcon={<ArrowLeftIcon />}
                  >
                    {t('common.back')}
                  </Button>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isInteractionLocked}
                  sx={{
                    width: 206,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 4
                  }}
                  endIcon={<ArrowRightIcon />}
                >
                  <span>{t('common.next')}</span>
                </Button>
              </Box>
              {renderFormInteractionOverlay()}
            </Box>
          </Box>
        </FormProvider>
        {recoverDialogs}
        {renderWaitlistDialog()}
      </>
    )
  }

  // ── Standard form layout ──────────────────────────────

  const formContent = (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Typography variant="h2" sx={{ color: 'primary.main', mb: 1 }}>
        {stepTitle}
      </Typography>
      {stepDescription && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {stepDescription}
        </Typography>
      )}

      {showWaitlistDebugTrigger && (
        <Box
          sx={{
            mb: 3,
            px: 1.5,
            py: 1.25,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'warning.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 700 }}>
            DEBUG LISTA D'ATTESA
          </Typography>
          <Button
            type="button"
            size="small"
            variant="text"
            onClick={() => void openWaitlistModalForDebug()}
          >
            Apri modale
          </Button>
        </Box>
      )}

      {/* Fields grid */}
      <Grid container spacing={3}>
        {stepConfig.fields
          .filter((field) => field.type !== 'hidden' && isFieldVisible(field, mergedValues))
          .map((field) => (
            <Grid key={field.key} size={{ xs: 12, md: field.colSpan ?? 12 }}>
              <DynamicField
                field={field}
                allValues={mergedValues}
                onEmailDraftFound={handleEmailDraftFound}
                onFieldLabelResolved={onFieldLabelResolved}
              />
            </Grid>
          ))}
      </Grid>

      {/* Navigation */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: isFirstStep ? 'flex-start' : 'space-between',
          alignItems: 'center',
          pt: 5,
          gap: 2
        }}
      >
        {!isFirstStep && onBack && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onBack}
            disabled={isInteractionLocked}
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
            startIcon={<ArrowLeftIcon />}
          >
            {t('common.back')}
          </Button>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={isInteractionLocked}
          sx={{
            width: 206,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 4
          }}
          endIcon={<ArrowRightIcon />}
        >
          <span>{stepConfig.isFinal ? t('common.submit') : t('common.next')}</span>
        </Button>
      </Box>
    </Box>
  )

  function renderWaitlistDialog() {
    return (
      <Dialog
        open={fullStructureModalOpen}
        onClose={closeFullStructureModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {t('availability.fullModal.title', {
            defaultValue: 'La sede_camplus_1 selezionata è al completo'
          })}
          <IconButton
            aria-label="close"
            onClick={closeFullStructureModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {/* ── Section 1: choose an alternative ── */}
            <Typography variant="body1" color="text.secondary">
              {t('availability.fullModal.description', {
                defaultValue:
                  "La soluzione scelta non è più disponibile. Puoi scegliere un'altra combinazione oppure chiedere di essere inserito in lista di attesa.",
                structure: fullStructureName
              })}
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth variant="standard" disabled={modalBusy}>
                  <InputLabel shrink>{t('fields.citta_1.label')}</InputLabel>
                  <Select
                    value={altCity}
                    onChange={(event) =>
                      void handleAlternativeCityChange(String(event.target.value))
                    }
                  >
                    {altCityOptions
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth variant="standard" disabled={modalBusy || !altCity}>
                  <InputLabel shrink>{t('fields.sede_camplus_1.label')}</InputLabel>
                  <Select
                    value={altStructure}
                    onChange={(event) =>
                      void handleAlternativeStructureChange(String(event.target.value))
                    }
                  >
                    {altStructureOptions
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          disabled={opt.isFull}
                          sx={
                            opt.isFull
                              ? {
                                  '&.Mui-disabled': {
                                    opacity: 1,
                                    color: 'text.primary'
                                  }
                                }
                              : undefined
                          }
                        >
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            <span>{opt.label}</span>
                            {opt.isFull && opt.badge && (
                              <Box
                                component="span"
                                sx={{
                                  px: 0.8,
                                  py: 0.2,
                                  borderRadius: 0.75,
                                  fontSize: '1.1rem',
                                  fontWeight: 700,
                                  lineHeight: 1.2,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.04em',
                                  color: '#FFFFFF',
                                  backgroundColor: opt.isFull ? 'error.light' : 'primary.light'
                                }}
                              >
                                {opt.badge}
                              </Box>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth variant="standard" disabled={modalBusy || !altStructure}>
                  <InputLabel shrink>{t('fields.sistemazione_1.label')}</InputLabel>
                  <Select
                    value={altRoom}
                    onChange={(event) => setAltRoom(String(event.target.value))}
                  >
                    {altRoomOptions
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Button
              type="button"
              variant="contained"
              fullWidth
              onClick={handleConfirmAlternative}
              disabled={modalBusy || !altCity || !altStructure || !altRoom}
              sx={{ mt: 1 }}
            >
              {modalBusy ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('availability.fullModal.confirmCta', {
                  defaultValue: 'Conferma nuova soluzione'
                })
              )}
            </Button>

            {/* ── Section 2: waitlist for original structure ── */}
            <Box
              sx={{
                mt: 2,
                py: 3,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, whiteSpace: 'pre-line' }}
              >
                <Trans
                  i18nKey="availability.fullModal.waitlistInfo"
                  values={{ structure: fullStructureName }}
                  components={{
                    strong: <strong />,
                    br: <br />
                  }}
                />
              </Typography>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                onClick={handleRequestWaitlist}
                disabled={modalBusy}
              >
                {t('availability.fullModal.waitlistCta')}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    )
  }

  if (stepConfig.showSideSummary && sideSummarySections.length > 0) {
    return (
      <>
        <FormProvider {...methods}>
          <Grid
            container
            component="form"
            onSubmit={handleFormSubmit}
            noValidate
            spacing={0}
            sx={{ width: '100%', position: 'relative' }}
          >
            <Grid size={{ xs: 12, lg: 7 }}>{formContent}</Grid>

            <Grid size={{ xs: 0, lg: 1 }} sx={{ display: { xs: 'none', lg: 'block' } }} />

            <Grid size={{ xs: 0, lg: 4 }} sx={{ display: { xs: 'none', lg: 'block' } }}>
              <Box
                sx={{
                  color: 'text.primary',
                  backgroundColor: 'white',
                  borderRadius: 2,
                  border: '3px solid',
                  p: 3,
                  position: 'sticky',
                  top: 100
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    color: 'text.primary',
                    mb: 2
                  }}
                >
                  {t('summary.sideTitle', { defaultValue: 'Riepilogo' })}
                </Typography>
                <Stack spacing={1.2}>
                  {sideSummarySections.map((section) => (
                    <Accordion
                      key={section.groupId}
                      expanded={expandedSideSummaryGroupId === section.groupId}
                      onChange={(_, expanded) =>
                        setExpandedSideSummaryGroupId(expanded ? section.groupId : null)
                      }
                      disableGutters
                      sx={{
                        boxShadow: 'none',
                        overflow: 'hidden',
                        '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          px: 0,
                          py: 0,
                          minHeight: 40,
                          flexDirection: 'row-reverse',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '& .MuiAccordionSummary-expandIconWrapper': {
                            mr: 1
                          },
                          '& .MuiAccordionSummary-content': {
                            my: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%'
                          }
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontFamily: '"Barlow Condensed", sans-serif',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: '#4c4c4c',
                            letterSpacing: '1.8px'
                          }}
                        >
                          {section.title}
                        </Typography>
                        <Button
                          component="span"
                          variant="text"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation()
                            const basePath = safePathname.includes('/booking-worker')
                              ? `/${locale}/booking-worker`
                              : `/${locale}/booking-student`
                            router.push(`${basePath}/${section.editStepSlug}/`)
                          }}
                          startIcon={<EditIcon sx={{ width: '1rem' }} />}
                          sx={{
                            ml: 0.5,
                            minWidth: 'auto',
                            px: 0,
                            py: 0,
                            fontWeight: 700,
                            fontSize: '1rem',
                            textDecoration: 'underline',
                            color: 'text.secondary'
                          }}
                        >
                          {t('summary.editLink', { defaultValue: 'Modifica' })}
                        </Button>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, py: 3 }}>
                        <Grid container spacing={1}>
                          {section.items.map((item, idx) => (
                            <Grid key={`${section.groupId}-${idx}`} size={{ xs: 12, sm: 6 }}>
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: '"Barlow", sans-serif',
                                    fontWeight: 600,
                                    fontSize: '1.2rem',
                                    color: '#B2B2B2',
                                    letterSpacing: '0.02em',
                                    display: 'block'
                                  }}
                                >
                                  {item.label}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: '"Barlow", sans-serif',
                                    fontWeight: 500,
                                    fontSize: '1.6rem',
                                    color: '#4C4C4C',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {item.value}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              </Box>
            </Grid>
            {renderFormInteractionOverlay()}
          </Grid>
        </FormProvider>
        {recoverDialogs}
        {renderWaitlistDialog()}
      </>
    )
  }

  return (
    <>
      <FormProvider {...methods}>
        <Grid
          container
          component="form"
          onSubmit={handleFormSubmit}
          noValidate
          sx={{ width: '100%', justifyContent: 'center', position: 'relative' }}
        >
          <Grid size={{ xs: 12, md: stepConfig.fullWidth ? 12 : 8 }}>{formContent}</Grid>
          {renderFormInteractionOverlay()}
        </Grid>
      </FormProvider>
      {recoverDialogs}
      {renderWaitlistDialog()}
    </>
  )
}

/* ── Helper: build summary items from previous step data ── */
function buildSideSummarySections(
  currentStep: FlowStepConfig,
  savedFormData: Record<string, Record<string, unknown>>,
  allSteps: FlowStepConfig[],
  t: (_key: string, _opts?: Record<string, unknown>) => string,
  remoteValueLabels: Record<string, string>
): {
  groupId: number
  title: string
  editStepSlug: string
  items: { label: string; value: string }[]
}[] {
  const sectionsByGroup = new Map<
    number,
    {
      groupId: number
      title: string
      editStepSlug: string
      items: { label: string; value: string }[]
    }
  >()

  for (const step of allSteps) {
    if (step.id >= currentStep.id) break
    const data = savedFormData[`step${step.id}`]
    if (!data) continue

    const stepItems: { label: string; value: string }[] = []

    for (const field of step.fields) {
      if (field.type === 'hidden' || field.type === 'checkbox') continue
      const val = data[field.key]
      if (val === undefined || val === '' || val === null) continue

      let display: string
      if (typeof val === 'boolean') {
        display = val ? t('common.yes') : t('common.no')
      } else if (val instanceof Date) {
        display = val.toLocaleDateString('it')
      } else if (
        (field.type === 'date' || field.type === 'date-picker-airbnb') &&
        typeof val === 'string' &&
        /^\d{4}-\d{2}-\d{2}/.test(val)
      ) {
        const [y, m, d] = val.split('T')[0].split('-')
        display = `${d}-${m}-${y}`
      } else if (field.options) {
        const opt = field.options.find((o) => o.value === String(val))
        display = opt ? t(opt.label, { defaultValue: opt.value }) : String(val)
      } else if (field.remoteOptions) {
        display = remoteValueLabels[`${step.id}:${field.key}:${String(val)}`] ?? String(val)
      } else {
        display = String(val)
      }

      stepItems.push({
        label: t(`fields.${field.key}.label`, {
          defaultValue: t(field.label, { defaultValue: field.label })
        }),
        value: display
      })
    }

    if (stepItems.length > 0) {
      const existing = sectionsByGroup.get(step.group)
      if (existing) {
        existing.items.push(...stepItems)
      } else {
        const groupTitle = step.summaryTitle
          ? t(step.summaryTitle)
          : t(`summary.groupTitles.${step.group}`, {
              defaultValue: t('summary.stepLabel', {
                step: step.group,
                defaultValue: `Step ${step.group}`
              })
            })
        sectionsByGroup.set(step.group, {
          groupId: step.group,
          title: groupTitle,
          editStepSlug: step.slug,
          items: [...stepItems]
        })
      }
    }
  }

  return Array.from(sectionsByGroup.values()).sort((a, b) => a.groupId - b.groupId)
}
